import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDialogDismiss } from "./ui/useDialogDismiss";
import type { DialogContent, DialogTone } from "../utils/errorPresentation";

/**
 * One dialog for the outcome of an action: refused, blocked, broken, about to
 * happen, or done.
 *
 * It replaces `window.alert` / `window.confirm` for actions whose failure has
 * structure — a list of lines at fault, a distinction between "you must fix
 * this" and "the server fell over", a raw message that support needs but a
 * reviewer should not have to read. A browser dialog can express none of that:
 * one font, no list, no tone, and a technical string presented with the same
 * weight as the instruction next to it.
 */

export type ActionDialogProps = DialogContent & {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Present ⇒ two buttons. Absent ⇒ a single dismiss button.
   *
   * The dialog does NOT close itself when this resolves — the handler owns
   * what happens next, so a one-way action can replace this dialog with a
   * report of what it did instead of vanishing and leaving the user guessing.
   * That means every handler MUST end by either calling `onClose` or opening
   * another dialog; one that quietly succeeds leaves this one sitting open
   * over a screen that has already changed underneath it.
   */
  onConfirm?: () => void | Promise<void>;
  /**
   * The Cancel button of a two-button dialog, when it must be told apart from
   * Escape, the × and the backdrop (which always call `onClose`). Defaults to `onClose`.
   */
  onCancel?: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Shown on the confirm button while an async onConfirm is in flight. */
  busyLabel?: string;
  /**
   * Arbitrary content between the summary and the `items` rows — e.g. the
   * quotation line-pick confirm's before -> after table
   * (`features/quotations/LinePickPreview.tsx`), which cannot be said as a
   * `DialogItem` list. `DialogContent` itself stays string-only (it also
   * feeds `describeApiError`'s copy-to-clipboard `details`, which a ReactNode
   * cannot serialize), so this is a second, presentation-only prop rather
   * than a widened `DialogContent` field.
   */
  body?: React.ReactNode;
};

type ToneStyle = {
  icon: string;
  iconWrap: string;
  accent: string;
  chip: string;
  confirmBtn: string;
  hintWrap: string;
};

const TONES: Record<DialogTone, ToneStyle> = {
  error: {
    icon: "!",
    iconWrap: "bg-rose-100 text-rose-600",
    accent: "bg-rose-500",
    chip: "bg-rose-50 text-rose-700 border-rose-200",
    confirmBtn: "bg-rose-600 hover:bg-rose-700 border-rose-600",
    hintWrap: "bg-rose-50 border-rose-200 text-rose-800",
  },
  blocked: {
    icon: "⏸",
    iconWrap: "bg-amber-100 text-amber-600",
    accent: "bg-amber-500",
    chip: "bg-amber-50 text-amber-700 border-amber-200",
    confirmBtn: "bg-amber-600 hover:bg-amber-700 border-amber-600",
    hintWrap: "bg-amber-50 border-amber-200 text-amber-800",
  },
  system: {
    icon: "⚡",
    iconWrap: "bg-slate-200 text-slate-700",
    accent: "bg-slate-500",
    chip: "bg-slate-100 text-slate-700 border-slate-300",
    confirmBtn: "bg-slate-700 hover:bg-slate-800 border-slate-700",
    hintWrap: "bg-slate-50 border-slate-200 text-slate-700",
  },
  confirm: {
    icon: "?",
    iconWrap: "bg-sky-100 text-sky-600",
    accent: "bg-sky-500",
    chip: "bg-sky-50 text-sky-700 border-sky-200",
    confirmBtn: "bg-primary-500 hover:bg-primary-600 border-primary-500",
    hintWrap: "bg-sky-50 border-sky-200 text-sky-800",
  },
  success: {
    icon: "✓",
    iconWrap: "bg-emerald-100 text-emerald-600",
    accent: "bg-emerald-500",
    chip: "bg-emerald-50 text-emerald-700 border-emerald-200",
    confirmBtn: "bg-emerald-600 hover:bg-emerald-700 border-emerald-600",
    hintWrap: "bg-emerald-50 border-emerald-200 text-emerald-800",
  },
};

export const ActionDialog: React.FC<ActionDialogProps> = ({
  isOpen,
  tone,
  title,
  summary,
  items,
  itemsHeading,
  hint,
  details,
  onClose,
  onConfirm,
  onCancel,
  confirmLabel = "Continue",
  cancelLabel = "Cancel",
  busyLabel = "Working…",
  body,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setBusy(false);
    setShowDetails(false);
    setCopied(false);
    const t = window.setTimeout(() => panelRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [isOpen]);

  // Escape cancels, never confirms — a destructive action must be clicked. It
  // closes only this dialog, never the Modal it sits over.
  const dismiss = useDialogDismiss(isOpen, onClose, busy);

  if (!isOpen) return null;

  const t = TONES[tone] || TONES.system;
  const rows = items || [];

  const runConfirm = async () => {
    if (!onConfirm) return onClose();
    try {
      setBusy(true);
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };

  const copyDetails = async () => {
    if (!details) return;
    try {
      await navigator.clipboard.writeText(`${title}\n${summary || ""}\n\n${details}`);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch (_) {
      /* clipboard blocked — the text is on screen anyway */
    }
  };

  // The overlay carries NO backdrop blur and only a light scrim, deliberately.
  // This dialog's job is to name rows that are still on screen behind it: a
  // refused Confirm & sync paints every offending cell `bg-rose-50` with a
  // `ring-rose-300` (see `utils/lineItemFlags.ts`), and the reviewer reads the
  // list here against the grid there. A 2px blur erases a 1px ring outright,
  // and at slate-900/45 rose-50 and white composite to within ~7/255 of each
  // other — the highlight the page had just drawn vanished, so a reviewer with
  // 55 flagged lines closed and reopened the dialog once per line to read them.
  // At /25 the text behind sits near 7:1 and the tint stays distinguishable,
  // while the panel is opaque white with shadow-2xl and still reads as modal.
  // Do not reintroduce the blur.
  const content = (
    <div
      {...dismiss}
      className="fixed inset-0 z-[95] flex items-center justify-center bg-slate-900/25 p-4 modal-overlay-safe"
      role="alertdialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className="w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-2xl outline-none
                   animate-[actionDialogIn_0.14s_ease-out] flex flex-col max-h-[85vh]"
      >
        <div className={`h-1 w-full shrink-0 ${t.accent}`} />

        <div className="flex items-start gap-3 px-5 pt-4 pb-3 shrink-0">
          <div
            className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${t.iconWrap}`}
            aria-hidden="true"
          >
            {t.icon}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
            {summary && (
              <p className="mt-1 text-[13px] leading-relaxed text-slate-600 whitespace-pre-line">
                {summary}
              </p>
            )}
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={() => !busy && onClose()}
            className="-mr-1 -mt-1 h-7 w-7 shrink-0 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <span className="text-lg leading-none">×</span>
          </button>
        </div>

        {body && (
          <div className="min-h-0 flex-1 overflow-y-auto border-t border-slate-100 px-5 py-3">
            {body}
          </div>
        )}

        {rows.length > 0 && (
          <div className="min-h-0 flex-1 overflow-y-auto border-t border-slate-100 px-5 py-3">
            {itemsHeading && (
              <div className="mb-2 flex items-baseline gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {itemsHeading}
                </span>
                <span className="text-xs text-slate-400">
                  {rows.length} {rows.length === 1 ? "item" : "items"}
                </span>
              </div>
            )}
            <ul className="space-y-1.5">
              {rows.map((row, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50/60 px-2.5 py-1.5"
                >
                  {row.label && (
                    <span
                      className={`mt-px shrink-0 rounded border px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide ${t.chip}`}
                    >
                      {row.label}
                    </span>
                  )}
                  <span className="min-w-0 text-[12.5px] leading-snug text-slate-700">
                    {row.text}
                    {row.note && <span className="text-slate-400"> — {row.note}</span>}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {hint && (
          <div className="px-5 pb-1 pt-2 shrink-0">
            <p className={`rounded-md border px-3 py-2 text-[12.5px] leading-relaxed ${t.hintWrap}`}>
              {hint}
            </p>
          </div>
        )}

        {details && (
          <div className="px-5 pt-2 shrink-0">
            <button
              type="button"
              onClick={() => setShowDetails((v) => !v)}
              className="text-xs font-medium text-slate-500 underline decoration-dotted underline-offset-2 hover:text-slate-700"
            >
              {showDetails ? "Hide technical details" : "Technical details"}
            </button>
            {showDetails && (
              <div className="mt-1.5 rounded-md border border-slate-200 bg-slate-900/95 p-2.5">
                <pre className="max-h-32 overflow-auto whitespace-pre-wrap break-words font-mono text-xs leading-snug text-slate-100">
                  {details}
                </pre>
                <button
                  type="button"
                  onClick={copyDetails}
                  className="mt-1.5 rounded border border-slate-600 px-1.5 py-0.5 text-xs font-medium text-slate-300 hover:bg-slate-800"
                >
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
            )}
          </div>
        )}

        <div className="mt-3 flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50 px-5 py-3 shrink-0">
          {onConfirm ? (
            <>
              <button
                type="button"
                onClick={onCancel ?? onClose}
                disabled={busy}
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-50"
              >
                {cancelLabel}
              </button>
              <button
                type="button"
                onClick={runConfirm}
                disabled={busy}
                className={`rounded-md border px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60 ${t.confirmBtn}`}
              >
                {busy ? busyLabel : confirmLabel}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className={`rounded-md border px-3 py-1.5 text-xs font-semibold text-white ${t.confirmBtn}`}
            >
              {confirmLabel === "Continue" ? "Close" : confirmLabel}
            </button>
          )}
        </div>

        <style>{`
          @keyframes actionDialogIn {
            from { opacity: 0; transform: translateY(6px) scale(0.985); }
            to   { opacity: 1; transform: translateY(0) scale(1); }
          }
        `}</style>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default ActionDialog;
